import { ActionIcon, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconLibraryPlus, IconPlus } from "@tabler/icons-react";
import AddStep from "./AddStep";
import { useReactFlow } from "reactflow";
import { useFlowStore } from "../../../../../../stores/WorkflowStore";
import {
  defaultNodeConfigs,
  nodeActions,
  nodesWithEvents,
} from "../flowSettings";
import { getDefaultZoom, getInheritedCredentials } from "../flowUtils";
import { handleLayout } from "../layouter";
import { useLanguage } from "../../../../../../stores/LanguageStore";

const NodePlus = ({ isBranch, data, xPos, yPos, id, isEvent, inStart }) => {
  const reactFlow = useReactFlow();
  const getId = useFlowStore((s) => s.getId);
  const setNodes = useFlowStore((s) => s.setNodes);
  const setEdges = useFlowStore((s) => s.setEdges);
  const initialId = useFlowStore((s) => s.initialId);
  const updateNodeData = useFlowStore((s) => s.updateNodeData);
  const lang = useLanguage((s) => s.language);  

  const nodes = useFlowStore((s) => s.nodes);
  const edges = useFlowStore((s) => s.edges);

  const handleAddNode = (newNodeType) => {
    let newNodes = [...nodes];
    let newEdges = [...edges];
    let newNodeId = getId().toString();
    let newEventNodeId = id;
    let child = nodes.find((x) => x.data.source === id);

    let needEvent =
      id === initialId ||
      (nodesWithEvents.includes(data?.actionType) &&
        nodesWithEvents.includes(newNodeType));

    let autoData = { ...defaultNodeConfigs };
    const inheritedCreds = getInheritedCredentials(
      nodes,
      id,
      newNodeType,
    );    

    if (inheritedCreds) {
      autoData.integration_id = inheritedCreds.integration_id;
      autoData.pool_id = inheritedCreds.pool_id;
      autoData.signature_id = inheritedCreds.signature_id;
    }
    //#endregion

    if (id === initialId && child?.data?.actionType !== "event") {
      let newEventNodeIdStart = getId().toString();
      newEdges = newEdges.filter((x) => x.source !== id);

      let newEventNodeStart = {
        id: newEventNodeIdStart,
        type: "action",
        data: {
          actionType: "event",
          source: initialId,
          when_minutes: 1,
          event_type: "all_contacts",
        },
        position: { x: xPos + 256, y: yPos },
      };
      newNodes.push(newEventNodeStart);

      let newEventEdgeStart = {
        id: `e-${initialId}-${newEventNodeIdStart}`,
        source: initialId,
        target: newEventNodeIdStart,
        data: { fromEvent: false },
      };
      newEdges.push(newEventEdgeStart);

      // We create an event node parent of initial action
      let newEdgeFromEventStart = {
        id: `e-${newEventNodeIdStart}-${child.id}`,
        source: newEventNodeIdStart,
        target: child.id,
        data: { fromEvent: true, error: false },
      };
      newEdges.push(newEdgeFromEventStart);

      updateNodeData(child.id, { source: newEventNodeIdStart });
    }

    // Add Event node if source is not initial
    if (needEvent) {
      newEventNodeId = getId().toString();
      const newEventNode = {
        id: newEventNodeId,
        type: "action",
        data:
          id === initialId
            ? {
                actionType: "event",
                source: initialId,
                when_minutes: 1,
                event_type: "all_contacts",
              }
            : {
                actionType: "event",
                source: id,
                event_type: data?.fromCall ? "call_custom" : undefined,
                error: data?.error,
              },
        position: { x: xPos + 256, y: yPos },
      };
      newNodes.push(newEventNode);

      const newEventEdge = {
        id: `e-${id}-${newEventNodeId}`,
        source: id,
        target: newEventNodeId,
        data: { fromEvent: false, error: data?.error },
      };
      newEdges.push(newEventEdge);
    }

    const newNode = {
      id: newNodeId,
      type: "action",
      data: {
        actionType: newNodeType,
        source: newEventNodeId,
        ...autoData,
        sending_weekends_allowed: id === initialId ? true : false,
        error: data?.error,
        event_type:
          id === initialId && newNodeType === "event"
            ? "all_contacts"
            : undefined,
        when_minutes:
          id === initialId && newNodeType === "event"
            ? 1
            : defaultNodeConfigs.when_minutes,
        shouldBeEvent:
          newNodeType !== "event" && nodeActions.includes(newNodeType)
            ? true
            : false,
      },
      position: { x: xPos + 256, y: yPos },
    };

    newNodes.push(newNode);

    if (!needEvent) {
      const newEdge = {
        id: `e-${id}-${newNodeId}`,
        source: id,
        target: newNodeId,
        data: { fromEvent: true, error: data?.error },
      };
      newEdges.push(newEdge);
    } else {
      const newEdge = {
        id: `e-${newEventNodeId}-${newNodeId}`,
        source: newEventNodeId,
        target: newNodeId,
        data: { fromEvent: true, error: data?.error },
      };
      newEdges.push(newEdge);
    }

    setNodes(newNodes);
    setEdges(newEdges);

    setTimeout(() => {
      handleLayout().then((r) => {
        if (r.nodes.length > 7) {
          reactFlow.fitView({
            maxZoom: getDefaultZoom() * 0.9,
            nodes: r.nodes.slice(-5),
            duration: 1000,
            padding: 0,
          });
        }
        // reactFlow.setViewport({
        //     x: reactFlow.getViewport().x - 380,
        //     y: reactFlow.getViewport().y,
        //     zoom: reactFlow.getZoom() - .5
        // }, { duration: 1000 }
        // );
      });
    }, 200);
  };

  const handleAddEvent = () => {
    let newNodes = [...nodes];
    let newEdges = [...edges];
    let newEventNodeId = getId().toString();
    //#endregion

    // Add Event node if source is not initial
    const newEventNode = {
      id: newEventNodeId,
      type: "action",
      data: {
        actionType: "event",
        source: id,
        event_type: data?.fromCall ? "call_custom" : undefined,
        error: data?.error,
      },
      position: { x: xPos, y: yPos },
    };
    newNodes.push(newEventNode);

    const newEdge = {
      id: `e-${id}-${newEventNodeId}`,
      source: id,
      target: newEventNodeId,
      data: { fromEvent: false, error: data?.error },
    };
    newEdges.push(newEdge);

    setNodes(newNodes);
    setEdges(newEdges);

    setTimeout(() => {
      handleLayout();
      setTimeout(() => {
        reactFlow.fitView({ nodes: nodes, duration: 1000 });
        // reactFlow.setViewport({
        //     x: reactFlow.getViewport().x - 380,
        //     y: reactFlow.getViewport().y,
        //     zoom: reactFlow.getZoom() - .5
        // }, { duration: 1000 }
        // );
      }, 400);
    }, 200);
  };

  const handleNewStep = (newNodeType) => {
    modals.closeAll();

    if (newNodeType === "event") {
      return handleAddEvent();
    }
    return handleAddNode(newNodeType);
  };

  const handleClick = (e) => {
    modals.open({
      children: (
        <AddStep
          handleActionClick={handleNewStep}
          noEvent={data?.actionType === "event"}
        />
      ),
      size: 1024,
    });
  };

  if (isBranch) {
    return (
      <Tooltip label={lang.workflows.add_step}>
        <ActionIcon
          variant="light"
          pos={"absolute"}
          right={isEvent ? 0 : -4}
          top={isEvent ? "76%" : "102%"}
          style={{ zIndex: 10 }}
          size={25}
          className="node-plus branch"
          radius={"sm"}
          onClick={handleClick}
        >
          <IconLibraryPlus size={18} />
        </ActionIcon>
      </Tooltip>
    );
  }
  return (
    <Tooltip label={lang.workflows.add_step}>
      <ActionIcon
        variant="light"
        pos={"absolute"}
        right={-56}
        top={"50%"}
        style={{
          transform: "translate(-50%, -50%)",
          zIndex: 10,
          border: "1px #8964f4",
          borderStyle: "dashed",
        }}
        size="md"
        className="node-plus"
        radius={"xl"}
        onClick={handleClick}
        color="white"
        bg={"#e9dcff"}
      >
        <IconPlus />
      </ActionIcon>
    </Tooltip>
  );
};

export default NodePlus;
